import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import ProgressButton from '../components/ProgressButton';
import CTAButton from '../components/CTAButton';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import paymentApi from '../service/paymentApi';
import { ToastContext } from '../contexts/ToastProvider';
import charToEntity from '../utils/charUtils';
import { getPrice, getFormattedPrice } from '../utils/priceUtils';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 20px;
  width: 100%;
  max-width: 400px;
`;

const Field = styled.div`
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;

  label {
    margin-bottom: 5px;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.text};
  }

  input, textarea {
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 5px;
    font-size: 14px;
  }

  textarea {
    height: 100px;
  }
`;

function Confirmation({ booking, name, phone, email, note, ethnicity, weight, height, bmi, riskFactors, contraindication, onBack }) {
  const { toast } = useContext(ToastContext)
  const [formData, setFormData] = useState({
    name: name || '',
    phone: phone || '',
    cpr: '',
    email: email || '',
    note: note || '',
  });
  const healthData = `
Etnicitet: ${ethnicity}, 
Vægt: ${weight}, 
Højde: ${height}, 
BMI: ${bmi}, 
Risikofaktorer: ${riskFactors.join(', ')}, 
Kontraindikationer: ${contraindication.join(', ')}
`;
  console.log(healthData)

  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return emailRegex.test(email);
  };

  // Danish CPR: DDMMYY-XXXX (hyphen optional). Format only — no mod-11 checksum,
  // since some valid modern CPR numbers fail mod-11. Per Nanna 2026-06-18.
  const validateCpr = (cpr) => /^\d{6}-?\d{4}$/.test(cpr.trim());

  const handleSubmit = async () => {
    if (!formData.name || !formData.phone || !formData.cpr || !formData.email) {
      toast.error('Venligst udfyld alle felter.');
      return;
    }

    if (!validateEmail(formData.email)) {
      toast.error('Indtast venligst en gyldig email.');
      return;
    }

    if (!validateCpr(formData.cpr)) {
      toast.error('Indtast venligst et gyldigt CPR-nummer (DDMMÅÅ-XXXX).');
      return;
    }

    if (!formData.phone) {
      toast.error('Indtast venligst et gyldigt telefonnummer.');
      return;
    }

    setIsLoading(true);

    try {
      const d = new Date();
      let time = d.getTime();
      const sanitizedCustomerName = charToEntity(formData.name);
      const sanitizedCustomerMobile = charToEntity(formData.phone);
      const sanitizedCustomerEmail = charToEntity(formData.email);
      const sanitizedBookingText = charToEntity(formData.note || '');
      const sanitizedHealthData = charToEntity(healthData);
      const response = await paymentApi.getOnPay({
        amount: getPrice() * 100,
        reference: 'ORDER' + time,
        customerName: sanitizedCustomerName,
        customerMobile: sanitizedCustomerMobile,
        customerEmail: sanitizedCustomerEmail,
        customerCpr: formData.cpr.trim(),
        bookingText: sanitizedBookingText,
        bookingDate: booking.date,
        bookingTimeStart: booking.time,
        bookingTimeEnd: getBookingEndTime(booking.time),
        healthInfo: sanitizedHealthData,
      });
      const link = response.links.payment_window
      window.location.href = `${link}`;
    } catch (error) {
      console.error('Error get OnPay:', error);
      // Slot filled up between selection and payment — ask for a new time.
      if (error?.data?.errorCode === 'SLOT_TAKEN') {
        toast.error('Den valgte tid er desværre ikke længere ledig. Vælg venligst et andet tidspunkt.');
        setIsLoading(false);
        onBack();
        return;
      }
      toast.error('Der opstod en fejl. Prøv venligst igen.');
    }

    setIsLoading(false);
  };

  const getBookingEndTime = (startTime) => {
    // Booking reserves 30 minutes total (15 min appointment + 15 min admin). Per Nanna.
    const [hour, minute] = startTime.split(':').map(Number);
    const total = hour * 60 + minute + 30;
    const endHour = Math.floor(total / 60) % 24;
    const endMinute = total % 60;

    return `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;
  };

  return (
    <Wrapper>
      <Card>
        <h1>Bekræft booking</h1>
        <p>
          <strong>Dato:</strong> {booking.date}
          <br />
          <strong>Tid:</strong> {booking.time} (24-timers format)
        </p>
        <Field>
          <label>Navn</label>
          <input
            type="text"
            placeholder="Indtast dit navn"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
          />
        </Field>
        <Field>
          <label>Telefonnummer</label>
          <PhoneInput
            country={'dk'}
            value={formData.phone}
            onChange={(value) => handleChange('phone', value)}
            isValid={(value) => value.length > 0}
          />
        </Field>
        <Field>
          <label>CPR-nummer</label>
          <input
            type="text"
            placeholder="DDMMÅÅ-XXXX"
            value={formData.cpr}
            onChange={(e) => handleChange('cpr', e.target.value)}
          />
        </Field>
        <Field>
          <label>Email</label>
          <input
            type="email"
            placeholder="Indtast din email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
          />
        </Field>
        <Field>
          <label>Noter</label>
          <textarea
            placeholder="Indtast eventuelle noter eller anmodninger"
            value={formData.note}
            onChange={(e) => handleChange('note', e.target.value)}
          />
        </Field>
        <Field>
          <label>Betalingens størrelse</label>
          <input
            name="amount"
            value={`${getFormattedPrice()} DKK`}
            disabled
          />

        </Field>
        <div>
          <ProgressButton text="Tilbage" onClick={onBack} />
          <CTAButton
            text={isLoading ? "Behandler..." : "Bekræft"}
            onClick={handleSubmit}
            disabled={isLoading}
          />
        </div>
      </Card>
    </Wrapper>
  );
}

export default Confirmation;
